export type ErosionTestConditionId = 'gentle-flow' | 'strong-flow';

export interface ErosionPredictionOption {
  readonly id: string;
  readonly label: string;
}

export interface ErosionTestCondition {
  readonly id: ErosionTestConditionId;
  readonly label: string;
  readonly flowStrength: 1 | 2;
}

export interface ErosionObservationResult {
  readonly id: string;
  readonly conditionId: ErosionTestConditionId;
  readonly materialMoved: 'a-little' | 'more';
  readonly channelChange: 'shallow' | 'deeper';
  readonly observation: string;
}

export interface ErosionInvestigationConfig {
  readonly id: string;
  readonly phenomenon: 'moving-water';
  readonly title: string;
  readonly sceneObservation: string;
  readonly investigationQuestion: string;
  readonly predictionPrompt: string;
  readonly predictionOptions: readonly ErosionPredictionOption[];
  readonly testConfig: {
    readonly variableId: 'water-flow-strength';
    readonly variablePrompt: string;
    readonly conditions: readonly ErosionTestCondition[];
    readonly constants: readonly string[];
  };
  readonly observationResults: Readonly<Record<ErosionTestConditionId, ErosionObservationResult>>;
  readonly supportedPredictionId: string;
  readonly comparisonPrompt: string;
  readonly evidenceStatement: string;
  readonly applicationPrompt: string;
  readonly completionWorldEvent: 'trail-restoration-1';
}

/**
 * Original Grade 2 science content. This is a simplified visual model, not a
 * real-world experiment procedure. Both runs keep the tray, loose soil amount,
 * and test time the same so the child changes only water-flow strength.
 */
export const WASHED_OUT_TRAIL_EROSION_1: ErosionInvestigationConfig = {
  id: 'washed-out-trail-1',
  phenomenon: 'moving-water',
  title: 'Follow the Moving Soil',
  sceneObservation:
    'The stream is cloudy, loose soil has shifted downhill, and a groove crosses the trail.',
  investigationQuestion:
    'How does the strength of flowing water change the amount of loose soil that moves?',
  predictionPrompt: 'What do you predict will happen when the water flows more strongly?',
  predictionOptions: [
    { id: 'strong-moves-more', label: 'Stronger flowing water will move more loose soil.' },
    { id: 'same-amount', label: 'Both water flows will move the same amount of loose soil.' },
    { id: 'gentle-moves-more', label: 'Gentle flowing water will move more loose soil.' },
  ],
  testConfig: {
    variableId: 'water-flow-strength',
    variablePrompt: 'Set the water flow, then run the model.',
    conditions: [
      { id: 'gentle-flow', label: 'Gentle flow', flowStrength: 1 },
      { id: 'strong-flow', label: 'Strong flow', flowStrength: 2 },
    ],
    constants: [
      'Use the same kind and amount of loose soil.',
      'Keep the tray slope the same.',
      'Run each flow for the same amount of time.',
    ],
  },
  observationResults: {
    'gentle-flow': {
      id: 'gentle-small-change',
      conditionId: 'gentle-flow',
      materialMoved: 'a-little',
      channelChange: 'shallow',
      observation: 'The gentle flow carries a little loose soil and leaves a shallow path.',
    },
    'strong-flow': {
      id: 'strong-larger-change',
      conditionId: 'strong-flow',
      materialMoved: 'more',
      channelChange: 'deeper',
      observation: 'The stronger flow carries more loose soil and leaves a deeper path.',
    },
  },
  supportedPredictionId: 'strong-moves-more',
  comparisonPrompt: 'Compare the two soil trays. What changed when the water flowed more strongly?',
  evidenceStatement:
    'In this model, stronger flowing water moved more loose soil and made a deeper path.',
  applicationPrompt:
    'Use the evidence to place the first protection stage beside the washed-out trail.',
  completionWorldEvent: 'trail-restoration-1',
};
